import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useQuery } from '@tanstack/react-query';
import { getJobs } from '../api/cron';
import { JobTable } from '../components/cron/JobTable';
import { CreateJobModal } from '../components/cron/CreateJobModal';
import { ImportExportPanel } from '../components/cron/ImportExportPanel';
import { usePermission } from '../hooks/usePermission';

export function CronJobs() {
  const canWrite = usePermission('cron.write');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cron', 'jobs'],
    queryFn: getJobs,
    refetchInterval: 30_000,
  });

  const jobs = data?.jobs ?? [];
  const managedJobs = jobs.filter((j) => j.source === 'webadmin');
  const systemJobs  = jobs.filter((j) => j.source === 'system');
  const activeJobs  = managedJobs.filter((j) => j.active);

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ScheduleIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Cron Jobs</Typography>
        </Stack>
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New job
          </Button>
        )}
      </Stack>

      {/* Stats strip */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <Chip
          label={isLoading ? <CircularProgress size={12} /> : `${managedJobs.length} managed`}
          color="primary"
          variant="outlined"
          size="small"
        />
        <Chip
          label={isLoading ? <CircularProgress size={12} /> : `${activeJobs.length} active`}
          color="success"
          variant="outlined"
          size="small"
        />
        <Chip
          label={isLoading ? <CircularProgress size={12} /> : `${managedJobs.length - activeJobs.length} paused`}
          color="warning"
          variant="outlined"
          size="small"
        />
        <Chip
          label={isLoading ? <CircularProgress size={12} /> : `${systemJobs.length} system`}
          variant="outlined"
          size="small"
        />
      </Stack>

      <JobTable jobs={jobs} loading={isLoading} />

      {canWrite && (
        <ImportExportPanel managedJobCount={managedJobs.length} />
      )}

      <CreateJobModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
